"use client";

import { useEffect, type ComponentType } from "react";
import nextDynamic from "next/dynamic";
import posthog from "posthog-js";
import { toast } from "sonner";
import { safeCapture } from "@/lib/analytics/safeCapture";
import { isChunkLoadError } from "./chunkErrors";

const RETRY_DELAY_MS = 300;

// Never let a reporting failure block resolve() below — a throw here would leave the
// Suspense promise permanently pending with no toast and no fallback ever rendering.
// safeCapture already guards the capture() call; captureException needs the same
// treatment since it isn't wrapped by safeCapture itself.
function reportChunkFailure(surface: string, error: unknown): void {
  safeCapture("chunk_load_failed", { surface, recovered: false });
  try {
    posthog.captureException(error);
  } catch {
    // Swallowed deliberately — same reasoning as safeCapture.ts's own doc comment.
  }
}

// The dynamic() component instance (and therefore whatever this resolves to) is
// cached by React.lazy for the page's lifetime — so resolving to a bare `() => null`
// after a permanent failure would leave every *subsequent* open attempt silently
// dead, with no toast and no telemetry: the exact TRA-342 symptom this file exists to
// fix, just made permanent instead of transient. Every current call site passes an
// `open: boolean` prop that a fresh click flips back to `true` even though the
// wrapping `xHasOpened` flag (and so this component instance) never remounts —
// watching that prop lets the toast re-fire on every attempt, not just the first.
function makeChunkLoadFailedComponent<P extends { open?: boolean }>(): ComponentType<P> {
  function ChunkLoadFailed(props: P) {
    useEffect(() => {
      if (!props.open) return;
      toast.error("Couldn't open that. Please reload the page.", {
        action: { label: "Reload", onClick: () => window.location.reload() },
      });
    }, [props.open]);
    return null;
  }
  return ChunkLoadFailed as ComponentType<P>;
}

/**
 * `next/dynamic({ ssr: false })`, but a failed chunk fetch degrades gracefully
 * instead of leaving the trigger (a click on a modal-opening button) doing nothing.
 * `next/dynamic` wraps the loader in `React.lazy`, which invokes it
 * exactly once and caches the rejection forever for that component instance — so
 * the retry below calls the raw `loader` itself (a fresh `import()` expression) once
 * before ever handing anything to `next/dynamic`/`React.lazy`.
 *
 * Only an actual chunk-load failure gets the retry/fallback treatment
 * (`isChunkLoadError`) — a real bug inside the lazily-loaded module (a
 * module-evaluation-time throw, a bad top-level import) re-throws immediately and
 * propagates to the nearest boundary exactly as it would have before this wrapper
 * existed. Treating every rejection as a chunk failure would both contaminate the
 * `chunk_load_failed` metric and hide a genuine bug behind a "please reload" toast
 * that reloading can never actually fix.
 *
 * `ssr: false` is hardcoded, not exposed as an option: every current call site is a
 * modal (AuthModal, EnquireModal, LodgeMapModal, ContactSheet, CampfireChatModal),
 * so there is no meaningful SSR output to preserve, and a network blip here costs
 * one retry, not a page-load stall. Most are click-gated behind a sticky
 * `xHasOpened` flag; the always-open QuizAuthGate
 * mounts `open` on first client render instead, which is fine — a chunk failure
 * there still resolves to the "reload" toast fallback. Reaching for this on a
 * component that does need SSR would silently lose it.
 */
export function dynamicWithRetry<P extends { open?: boolean }>(
  loader: () => Promise<ComponentType<P>>,
  { surface }: { surface: string },
): ComponentType<P> {
  return nextDynamic(
    () =>
      loader().catch((firstError: unknown) => {
        if (!isChunkLoadError(firstError)) throw firstError;

        return new Promise<{ default: ComponentType<P> }>((resolve) => {
          setTimeout(() => {
            loader().then(
              (Component) => {
                safeCapture("chunk_load_failed", { surface, recovered: true });
                resolve({ default: Component });
              },
              (secondError: unknown) => {
                reportChunkFailure(surface, secondError ?? firstError);
                // Resolve to a component rather than rejecting: rejecting here would
                // surface Next's internal Suspense/lazy error path and tear down
                // whatever page the user was already on. See
                // makeChunkLoadFailedComponent's own doc comment for why this isn't
                // just `() => null`.
                resolve({ default: makeChunkLoadFailedComponent<P>() });
              },
            );
          }, RETRY_DELAY_MS);
        });
      }),
    { ssr: false },
  );
}
